import { Component, Output, EventEmitter } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { slideDownUp } from './../../../../shared/animations';
import { CookieService } from '../../../../core/services/cookie-service/cookie.service';
import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/core/services/auth-service/auth.service';
import { NotificationService } from 'src/app/core/services/notification-service/notification.service';
import { ProfileService } from 'src/app/core/services/profile-service/profile.service';

interface MenuItem {
  id: string;
  label: string;
  icon?: string;
  link?: string;
  externalLink?: string;
  submenu?: MenuItem[];
  isHeader?: boolean;
  disabled?: boolean;
  hide?: boolean;
}

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  animations: [slideDownUp],
})
export class SidebarComponent {
  active = false;
  store: any;
  activeDropdown: string[] = [];
  parentDropdown: string = '';
  appVersion: string = environment.version;
  isLoading:boolean = false;
  isCollapsed: boolean = false;

  @Output() sidebarCollapsed = new EventEmitter<boolean>();

  menuItems: MenuItem[] = [
    // ------ Accueil -----------------------
    {
      id: 'dashboard',
      label: 'Accueil',
      icon: 'dashboard',
      link: '/dashboard'
    },

    // ------ Compte et Adhésion -----------------------
    {
      id: 'header-compte-adhesion',
      label: 'Compte et Adhésion',
      isHeader: true
    },
    {
      id: 'mon-compte',
      label: 'Mon compte',
      icon: 'mc',
      link: '/mon-compte'
    },
    {
      id: 'ma-famille',
      label: 'Ma famille',
      icon: 'fea',
      link: '/ma-famille/liste'
    },
    {
      id: 'mes-demandes',
      label: 'Mes demandes d’adhésion',
      icon: 'adld',
      link: '/adhesion/liste'
    },
    {
      id: 'mes-cotisations',
      label: 'Paiement des cotisations',
      icon: 'mc',
      link: '/cotisations/liste',
      hide: true
    },

    // ------ Prestations -----------------------
    {
      id: 'header-prestations',
      label: 'Prestations',
      isHeader: true
    },
    {
      id: 'fds',
      label: 'Soins ambulatoires',
      icon: 'fds',
      link: '/fds/liste'
    },
    {
      id: 'pecs',
      label: 'Prises en charge',
      icon: 'pec',
      link: '/pecs/liste'
    },
    {
      id: 'ep',
      label: 'Ententes préalables',
      icon: 'ep',
      link: '/ep/liste'
    },
    {
      id: 'afld',
      label: 'Affections de Longue Durée',
      icon: 'adld',
      link: '/afld/liste'
    },

    // ------ Assistance -----------------------
    {
      id: 'header-assistance',
      label: 'Assistance',
      isHeader: true
    },
    {
      id: 'reclamations',
      label: 'Réclamations',
      icon: 'recla',
      link: '/reclamations/liste'
    },
    {
      id: 'formulaires',
      label: 'Formulaires',
      icon: 'form',
      link: '/assistance/formulaires'
    },
    {
      id: 'documents-utiles',
      label: 'Documents utiles',
      icon: 'form',
      externalLink: 'https://tech-lamas.ma/reglementation/'
    },
    {
      id: 'contact',
      label: 'Contactez-nous',
      icon: 'contact',
      link: '/assistance/contact'
    },
    {
      id: 'guide',
      label: 'Guide d\'utilisation',
      icon: 'guide',
      link: '/assistance/guide',
      disabled: true
    }
  ];

  // ---- exemple : Menu items with submenu
  // {
  //   id: 'mon-compte',
  //   label: 'Mon compte',
  //   icon: 'mc',
  //   submenu: [
  //     { id: 'mon-profil', label: 'Mon profil', link: '/mon-profil' },
  //     { id: 'parametres', label: 'Paramètres', link: '/parametres' }
  //   ]
  // }

  constructor(
    public translate: TranslateService,
    public storeData: Store<any>,
    public router: Router,
    private cookieService: CookieService,
    private authService: AuthService,
    private notificationService: NotificationService,
    private profileService: ProfileService
  ) {
    this.initStore();

    // Subscribe to router events to collapse menus on navigation
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.activeDropdown = [];
        this.setActiveDropdown();

        // Fermer la sidebar mobile automatiquement après navigation
        if (window.innerWidth < 1024) {
          this.storeData.dispatch({ type: 'closeSidebar' });
        }
      }
    });
  }

  async initStore() {
    this.storeData
      .select((d) => d.index)
      .subscribe((d) => {
        this.store = d;
      });
  }

  ngOnInit() {
    this.setActiveDropdown();

    // Sur mobile/tablet, forcer isCollapsed = false
    if (window.innerWidth < 1024) {
      this.isCollapsed = false;
      this.sidebarCollapsed.emit(false);
    }

    window.addEventListener('resize', () => {
      if (window.innerWidth < 1024 && this.isCollapsed) {
        this.isCollapsed = false;
        this.sidebarCollapsed.emit(false);
      }
    });

    // Subscribe to router events to update active menu items
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        // Reset and recalculate active dropdowns on navigation
        this.setActiveDropdown();
      }
    });

    // Show cotisations only if type is 'inactif' (hidden by default via hide: true)
    this.profileService.getAdherentType().subscribe({
      next: (res: any) => {
        const adhType = res?.ADH_TYPE?.toLowerCase();
        if (adhType === 'inactif') {
          const item = this.menuItems.find(i => i.id === 'mes-cotisations');
          if (item) item.hide = false;
        }
      },
      error: (error) => {
        console.warn('Could not fetch adherent type, keeping cotisations hidden:', error);
      }
    });
  }

  toggleMobileMenu() {
    this.activeDropdown = [];
    if (window.innerWidth < 1024) {
      this.storeData.dispatch({ type: 'closeSidebar' });
    }
  }

  isRouteActive(route: string | undefined): boolean {
    if (!route) return false;

    // Remove query params from URL before comparison
    const urlWithoutParams = this.router.url.split('?')[0];

    // For exact route matching, compare the full paths
    if (route.startsWith('/assistance/')) {
      // For assistance routes, we need exact path matching
      return urlWithoutParams === route;
    }

    // For other routes, use module-based matching
    const moduleFromRoute = route.split('/')[1];
    const moduleFromUrl = urlWithoutParams.split('/')[1];

    return moduleFromRoute === moduleFromUrl;
  }

  hasActiveSubmenuItem(item: MenuItem): boolean {
    if (!item.submenu) return false;
    
    const currentModule = this.router.url.split('/')[1];
    
    return item.submenu.some(subItem => {
      if (!subItem.link) return false;
      const subItemModule = subItem.link.split('/')[1];
      return currentModule === subItemModule;
    });
  }

  onSubmenuItemClick(menuId: string) {
    this.toggleMobileMenu();
  }

  setActiveDropdown() {
    const pathname = window.location.pathname;

    // Check if we need to auto-expand any menu that contains the current route
    for (const item of this.menuItems) {
      if (item.submenu) {
        for (const subItem of item.submenu) {
          if (subItem.link && pathname.includes(subItem.link)) {
            // Only add it to activeDropdown if it's not already there
            if (!this.activeDropdown.includes(item.id)) {
              this.activeDropdown.push(item.id);
            }
            break;
          }
        }
      }
    }
  }

  toggleAccordion(name: string) {
    if (this.activeDropdown.includes(name)) {
      // If clicking on an already open menu, just close it
      this.activeDropdown = this.activeDropdown.filter((d) => d !== name);
    } else {
      // Close all other open menus first
      this.activeDropdown = [];
      // Then open the clicked menu
      this.activeDropdown.push(name);
    }
  }

  toggleSidebar() {
    this.isCollapsed = !this.isCollapsed;
    this.sidebarCollapsed.emit(this.isCollapsed);
  }

  logout() {
    this.storeData.dispatch({ type: 'toggleMainLoader', payload: true });
    this.authService.LogOutUser().subscribe({
      next: (response) => {
        this.storeData.dispatch({ type: 'toggleMainLoader', payload: false });

        this.cookieService.removeCookies();
        this.router.navigate(['/login']);
      },
      error: (error) => {
        this.storeData.dispatch({ type: 'toggleMainLoader', payload: false });
        this.notificationService.error('Erreur', 'La déconnexion a échoué. Veuillez réessayer.');
      }
    });
  }
}